import React from "react";
import {
  I2cInput,
  I2cCheckboxGroup,
  I2cListItem,
  I2cCheckbox,
} from "@webcomponents/i2cwebcomponents/dist/react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import ErrorMessage from "../../../Components/SharedComponent/ErrorMessage/ErrorMessage";
import { otherServices } from "./stringUtils";
import "../Prepaid.scss";

const OtherServices = ({ setAddFormData, addFormData, setFormValidStates, formDataJson, isFetching }) => {
  const validationSchema = Yup.object({
    value_added_service_other_service_additional_setup_monthly_minimum: Yup.array()
      .optional(),
    value_added_service_other_service_additional_setup_monthly_minimum_other: Yup.string().when("value_added_service_other_service_additional_setup_monthly_minimum", {
      is: (services) => services && services.includes("other"),
      then: () =>
        Yup.string().optional(),
      otherwise: () => Yup.string().optional(),
    }),
  });

  // Initialize React Hook Form with Yup resolver
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    mode: "all",
    reValidateMode: "all",
    shouldUseNativeValidation: false,
    criteriaMode: "all",
    defaultValues: {
      value_added_service_other_service_additional_setup_monthly_minimum: [],
      value_added_service_other_service_additional_setup_monthly_minimum_other: "",
    },
  });

  // Controlls destructuring from the method
  const {
    control,
    formState: { errors },
  } = methods;

  const fields = [
    "value_added_service_other_service_additional_setup_monthly_minimum",
    "value_added_service_other_service_additional_setup_monthly_minimum_other",
  ];

  // Load existing form data
  React.useEffect(() => {
    if (!isFetching && Object.keys(formDataJson ?? {}).length) {
      fields.forEach((field) => {
        const value = formDataJson[4]?.["4_5"]?.[field];
        if (value !== undefined && value !== null) {
          methods.setValue(field, value);
        }
      });
      methods.trigger();
      handleValueChanges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching]);

  // Trigger validation when component mounts
  React.useEffect(() => {
    methods.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleValueChanges = () => {
    const values = methods.getValues();
    setAddFormData((prev) => ({
      ...prev,
      4: {
        ...prev[4],
        "4_5": { ...values },
      },
    }));
  };

  // Update form validation state when errors change
  React.useEffect(() => {
    const errorsLength = Object.keys(errors).length;
    if (errorsLength === 0) setFormValidStates("4_5", true);
    else setFormValidStates("4_5", false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);



  const handleCheckboxGroupChange = (event) => {
    let selectedServiceValue;
    if (!event.length) selectedServiceValue = event.target.value;
    else selectedServiceValue = event;

    let updatedSelectedServices = [...methods.watch("value_added_service_other_service_additional_setup_monthly_minimum")];

    if (updatedSelectedServices.includes(selectedServiceValue)) {
      // If the card is already selected, remove it
      updatedSelectedServices = updatedSelectedServices.filter(
        (card) => card !== selectedServiceValue,
      );
      // Clear "other" field when "other" checkbox is unchecked
      if (selectedServiceValue === "other") {
        methods.setValue("value_added_service_other_service_additional_setup_monthly_minimum_other", "");
      }
    } else {
      // If the card is not selected, add it
      updatedSelectedServices.push(selectedServiceValue);
    }
    methods.setValue("value_added_service_other_service_additional_setup_monthly_minimum", updatedSelectedServices, { shouldValidate: true });
    handleValueChanges();
  };

  return (
    <React.Fragment>
      <div i2c-grid="grid gap-sm">
        <div i2c-grid="12">
          <div className="single-field-wrapper">
            <Controller
              name="value_added_service_other_service_additional_setup_monthly_minimum"
              control={control}
              render={({ field }) => (
                <>
                  <I2cCheckboxGroup {...field} className="centeredCheckbox">
                    <div i2c-grid="grid gap-sm">
                      {otherServices.map((card, index) => (
                        <I2cListItem
                          key={index}
                          i2c-grid="4@md"
                          variant="receipt"
                          label={card.label}
                          sublabel=""
                          value=""
                          className="checkbox-item"
                          type="checkbox"
                          checked={methods
                            .watch("value_added_service_other_service_additional_setup_monthly_minimum")
                            .includes(card.value)}
                          onI2cCheckboxClicked={(e) => {
                            handleCheckboxGroupChange(card.value);
                          }}
                        >
                          <I2cCheckbox
                            slot="prefix"
                            name="value_added_service_other_service_additional_setup_monthly_minimum"
                            value={card.value}
                            label=""
                            filled
                            ignoreTab
                            checked={methods
                              .watch("value_added_service_other_service_additional_setup_monthly_minimum")
                              .includes(card.value)}
                            className="customCheckbox"
                          ></I2cCheckbox>
                        </I2cListItem>
                      ))}
                    </div>
                  </I2cCheckboxGroup>
                  {errors.value_added_service_other_service_additional_setup_monthly_minimum && (
                    <ErrorMessage show={true}
                      message={errors.value_added_service_other_service_additional_setup_monthly_minimum.message}
                    />
                  )}
                </>
              )}
            />
          </div>
        </div>
     
        {methods.watch("value_added_service_other_service_additional_setup_monthly_minimum").includes("other") && (
          <div i2c-grid="4@md">
            <div className="single-field-wrapper">
              <Controller
                name="value_added_service_other_service_additional_setup_monthly_minimum_other"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    name={"value_added_service_other_service_additional_setup_monthly_minimum_other"}
                    className="single-input-field"
                    label="Please Specify"
                    placeholder="Enter Details"
                    onI2cInput={(e) => {
                      const value = e.target.value;
                      methods.setValue("value_added_service_other_service_additional_setup_monthly_minimum_other", value, { shouldValidate: true });
                      handleValueChanges();
                    }}
                    size="large"
                    invalid={errors.value_added_service_other_service_additional_setup_monthly_minimum_other ? true : false}
                  />
                )}
              />
              {errors.value_added_service_other_service_additional_setup_monthly_minimum_other && (
                <ErrorMessage show={true}
                  message={errors.value_added_service_other_service_additional_setup_monthly_minimum_other.message}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default OtherServices;
